import { postAction, isOffline, isConnectivityError } from '../api/client';
import type { SubmitResponse, SubmitOk, AbnormalReport } from '../api/types';
import type { ScheduleState } from '../lib/schedule';
import { isErr } from '../api/types';
import { capturedNowIso } from '../lib/serverTime';
import { enqueue, listOutbox, updateOutbox, removeOutbox, type OutboxItem } from '../db/outbox';
import { currentSession } from '../auth/session';
import { bustCache } from '../lib/swr';
import { APP_VERSION } from '../config';

/** A new reading landed on the server — drop the read caches it affects. */
function bustReadCaches(): void {
  ['operatorDashboard', 'adminDashboard', 'dailyLog', 'readings', 'trend'].forEach(bustCache);
}

export interface SubmitInput {
  tagCode: string;
  equipmentName: string;
  location: string;
  values: Record<string, number | string>;
  remarks: string;
  confirmedAbnormal: boolean;
}

export type SubmitOutcome =
  | { kind: 'synced'; data: SubmitOk }
  | { kind: 'queued'; item: OutboxItem }
  | { kind: 'abnormal'; abnormal: AbnormalReport }
  | { kind: 'blocked'; message: string; schedule?: ScheduleState }
  | { kind: 'invalid'; message: string }
  | { kind: 'auth' }
  | { kind: 'error'; message: string };

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function buildItem(input: SubmitInput, submissionId: string): OutboxItem {
  const ses = currentSession();
  return {
    submissionId,
    createdAt: capturedNowIso(),
    clientCapturedAt: capturedNowIso(),
    tagCode: input.tagCode,
    equipmentName: input.equipmentName,
    location: input.location,
    operatorId: ses?.operatorId ?? '',
    operatorName: ses?.operatorName ?? '',
    values: input.values,
    remarks: input.remarks,
    confirmedAbnormal: input.confirmedAbnormal,
    status: 'pending',
    attempts: 0,
  };
}

/**
 * Optimistic path: write the reading to the local outbox and return at once.
 * The sync controller pushes it to the server within moments (it flushes on any
 * outbox change). The caller must have already run the local checks that the
 * server also runs — required/numeric validation, the interval gate, and the
 * abnormal-threshold confirmation — because a reading that fails them on the
 * server comes back as "rejected" in Pending readings, not as an inline error.
 */
export async function submitLocalFirst(input: SubmitInput, submissionId: string): Promise<OutboxItem> {
  const item = buildItem(input, submissionId);
  await enqueue(item);
  return item;
}

/** Submit online if possible; otherwise queue for later sync. */
export async function submitOrQueue(input: SubmitInput, submissionId?: string): Promise<SubmitOutcome> {
  const id = submissionId ?? newId();
  const item = buildItem(input, id);
  const ses = currentSession();
  if (!ses) return { kind: 'auth' };

  if (isOffline()) {
    await enqueue(item);
    return { kind: 'queued', item };
  }

  try {
    const res = await postAction<SubmitResponse>('submitReading', {
      token: ses.token,
      submissionId: id,
      tagCode: input.tagCode,
      values: input.values,
      remarks: input.remarks,
      confirmedAbnormal: input.confirmedAbnormal,
      source: 'online',
      appVersion: APP_VERSION,
    });

    if (!isErr(res)) { bustReadCaches(); return { kind: 'synced', data: res }; }

    switch (res.error) {
      case 'ABNORMAL_UNCONFIRMED':
        return { kind: 'abnormal', abnormal: res.abnormal! };
      case 'INTERVAL_BLOCKED':
        return { kind: 'blocked', message: res.message, schedule: res.schedule };
      case 'VALIDATION':
        return { kind: 'invalid', message: res.problems?.join('\n') ?? res.message };
      case 'AUTH_REQUIRED':
      case 'AUTH_INVALID':
        return { kind: 'auth' };
      default:
        return { kind: 'error', message: res.message };
    }
  } catch (e) {
    if (isConnectivityError(e)) {
      await enqueue(item);
      return { kind: 'queued', item };
    }
    return { kind: 'error', message: e instanceof Error ? e.message : String(e) };
  }
}

/** Push every pending outbox item to the server. Returns counts. */
export async function flushOutbox(): Promise<{ synced: number; rejected: number; pending: number }> {
  const ses = currentSession();
  let synced = 0, rejected = 0, pending = 0;
  const items = (await listOutbox()).filter((i) => i.status === 'pending' || i.status === 'syncing');
  if (!ses || isOffline()) return { synced: 0, rejected: 0, pending: items.length };

  for (const item of items) {
    await updateOutbox(item.submissionId, { status: 'syncing' });
    try {
      const res = await postAction<SubmitResponse>('submitReading', {
        token: ses.token,
        submissionId: item.submissionId,
        tagCode: item.tagCode,
        values: item.values,
        remarks: item.remarks,
        confirmedAbnormal: item.confirmedAbnormal,
        source: 'offline',
        clientCapturedAt: item.clientCapturedAt,
        appVersion: APP_VERSION,
      });

      if (!isErr(res)) {
        await removeOutbox(item.submissionId);
        synced++;
        continue;
      }
      if (res.error === 'NETWORK') {
        await updateOutbox(item.submissionId, { status: 'pending', attempts: item.attempts + 1, lastError: res.message });
        pending++;
        continue;
      }
      // permanent rejection (blocked / validation / abnormal-unconfirmed / auth)
      await updateOutbox(item.submissionId, {
        status: 'rejected',
        rejectReason: res.error === 'INTERVAL_BLOCKED'
          ? 'Rejected on sync: another reading was recorded within the minimum interval.'
          : `Rejected on sync: ${res.message}`,
      });
      rejected++;
    } catch (e) {
      await updateOutbox(item.submissionId, {
        status: 'pending',
        attempts: item.attempts + 1,
        lastError: e instanceof Error ? e.message : String(e),
      });
      pending++;
      // server unreachable/slow — stop hammering; the rest retry next cycle
      if (isConnectivityError(e)) { pending += items.length - items.indexOf(item) - 1; break; }
    }
  }
  if (synced) bustReadCaches();
  return { synced, rejected, pending };
}
